import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import usePermissions from '../../auth/usePermissions';
import '../../styles/ProductsServices.css';

import { useProducts } from './useProducts';
import { useProductFilters } from './useProductFilters';

import FiltersBar from './FiltersBar';
import ProductsTable from './ProductsTable';
import Pagination from './Pagination';
import ProductModalManager from './ProductModalManager';
import ProductFolioModal from '../../components/ProductsServices/ProductFolioModal';
import notify from '../../utils/notify';
import usePageSize, { PAGE_SIZE_OPTIONS } from '../../hooks/usePageSize';

// The catalogue has always shown 5 items a page, so that stays its default.
const PRODUCT_PAGE_SIZE_OPTIONS = [5, ...PAGE_SIZE_OPTIONS];

export default function ProductsServicesPage() {
  const { permissions } = usePermissions();
  const canEdit = permissions.createProductsServices !== false;

  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [folioItem, setFolioItem] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { items, fetchItems, toggleFavorite, deleteItem } = useProducts();

  const [pageSize, setPageSize] = usePageSize('products', PRODUCT_PAGE_SIZE_OPTIONS);
  const { currentItems, pagination, filters, setFilters } = useProductFilters(items, pageSize);

  useEffect(() => {
    const editItemId = location.state?.editItemId;
    if (!editItemId || items.length === 0) return;

    const match = items.find((item) => item.itemId === editItemId);
    if (match) {
      setEditingItem(match);
      setShowForm(false);
      navigate('/products', { replace: true, state: {} });
    }
  }, [items, location.state, navigate]);

  function toggleSort(field) {
    setFilters((f) => ({
      ...f,
      sortField: field,
      sortOrder: f.sortField === field && f.sortOrder === 'desc' ? 'asc' : 'desc',
      page: 1,
    }));
  }

  async function handleConfirmDelete() {
    if (!deleteCandidate?.itemId) return;
    setIsDeleting(true);
    try {
      await deleteItem(deleteCandidate.itemId);
      if (editingItem?.itemId === deleteCandidate.itemId) setEditingItem(null);
      if (folioItem?.itemId === deleteCandidate.itemId) setFolioItem(null);
      setDeleteCandidate(null);
      notify.success('Item deleted.');
    } catch (err) {
      console.error('Delete product/service error', err);
      notify.error(err?.message || 'Unable to delete item.');
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="ps-page">
      <div className="header-row">
        <h2 className="section-title">Products & Services</h2>
        {canEdit && (
          <button
            className="record-add-btn"
            onClick={() => {
              setEditingItem(null);
              setShowForm(true);
            }}
          >
            + Add New
          </button>
        )}
      </div>

      <FiltersBar filters={filters} setFilters={setFilters} items={items} />

      <ProductsTable
        items={currentItems}
        onEdit={canEdit ? setEditingItem : null}
        onToggleFavorite={toggleFavorite}
        onOpenFolio={setFolioItem}
        onDelete={canEdit ? setDeleteCandidate : null}
        canEdit={canEdit}
        sortField={filters.sortField}
        sortOrder={filters.sortOrder}
        onSort={toggleSort}
      />

      <Pagination
        {...pagination}
        pageSize={pageSize}
        pageSizeOptions={PRODUCT_PAGE_SIZE_OPTIONS}
        onPageSizeChange={(size) => {
          setPageSize(size);
          pagination.setPage(1);
        }}
      />

      <ProductModalManager
        editingItem={editingItem}
        showForm={showForm}
        onClose={() => {
          setEditingItem(null);
          setShowForm(false);
        }}
        refreshItems={fetchItems}
      />

      {folioItem && <ProductFolioModal item={folioItem} onClose={() => setFolioItem(null)} />}

      {deleteCandidate && (
        <div
          className="ps-delete-overlay"
          onClick={() => (isDeleting ? null : setDeleteCandidate(null))}
        >
          <div
            className="ps-delete-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3>Delete {deleteCandidate.type === 'service' ? 'Service' : 'Product'}?</h3>
            <p>
              This will remove <strong>{deleteCandidate.name || 'this item'}</strong> from your
              Products &amp; Services list. This cannot be undone.
            </p>
            <div className="ps-delete-modal-actions">
              <button
                type="button"
                className="ps-delete-cancel-btn"
                onClick={() => setDeleteCandidate(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ps-delete-confirm-btn"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
